import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import { Box } from '@mui/material';
import { appTheme } from 'common/theme';
import { cacheRtl } from 'theme';
import { generateTestId } from 'app/functions';
import { addPopup } from 'redux/actions';
import { useGetHotYoterStatePanelQuery } from 'redux/api/content.api';
import * as popups from 'constants/popup-types';
import { HotYoterEligiblesPopupPanel } from './components/HotYoterEligiblesPopupPanel';
import { getEligiblesContent, canViewPurchasedHotYoterBenefits } from './utils/getEligiblesContent';
import { usePopupTypography } from './hooks/usePopupTypography';
import { usePopupLayout } from './hooks/usePopupLayout';
import { normalizePurchasedBenefits } from '../../../hot_yoter/utils/parseHotYoterPageResponse';

const hasVisibleContent = (content) =>
    Boolean(content?.title || content?.subtitle || content?.actionLabel);

const HotYoterEligiblesPopupContent = ({
    userData,
    hideActionLabel = false,
    onDetailsPress,
    onChooseBenefitPress,
    testID = 'hot-yoter-eligibles-popup',
}) => {
    const dispatch = useDispatch();
    const [isVisible, setIsVisible] = useState(true);
    const translations = useSelector((store) => store.config.Translations);
    const typography = usePopupTypography();
    const layout = usePopupLayout();
    const { data: statePanel } = useGetHotYoterStatePanelQuery(undefined, {
        skip: !userData,
    });
    const content = useMemo(
        () => getEligiblesContent(statePanel?.hotYoterStateTexts, userData, undefined, translations),
        [statePanel?.hotYoterStateTexts, userData, translations],
    );
    const purchasedBenefits = useMemo(() => {
        if (!canViewPurchasedHotYoterBenefits(userData)) {
            return [];
        }

        return normalizePurchasedBenefits(statePanel?.purchasedBenefits);
    }, [userData, statePanel?.purchasedBenefits]);

    if (!isVisible || !hasVisibleContent(content)) {
        return null;
    }

    const handleSubsidyConsentPress = () => {
        const isMarketingApproved = userData?.maStatus || userData?.isMarketingApproved;

        dispatch(addPopup({
            type: popups.DATA_SHARING_SUBSIDY_POPUP,
            isShowSubsidyCheckbox: true,
            isShowMarketingCheckbox: !isMarketingApproved,
            isShowDataSharingCheckbox: !userData?.consentedToDataSharing,
            headerText: translations?.data_sharing_popup_header,
            subtitle: translations?.consents_subsidy_modal_content,
        }));
    };

    const handleActionPress = () => {
        if (content.actionType === 'details') {
            onDetailsPress?.();
            return;
        }
        if (content.actionType === 'choose_benefit') {
            onChooseBenefitPress?.();
            return;
        }
        if (content.actionType === 'subsidy_consent') {
            handleSubsidyConsentPress();
        }
    };

    return (
        <Box
            role="dialog"
            aria-live="polite"
            dir="rtl"
            testId={testID}
            sx={{
                zIndex: 110,
                pointerEvents: 'none',
                ...layout.containerSx,
            }}
        >
            <HotYoterEligiblesPopupPanel
                content={content}
                purchasedBenefits={purchasedBenefits}
                hideActionLabel={hideActionLabel}
                typography={typography}
                layout={layout}
                onClose={() => setIsVisible(false)}
                onActionPress={handleActionPress}
                testID={generateTestId(testID, 'panel')}
            />
        </Box>
    );
};

const HotYoterEligiblesPopup = (props) => (
    <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={appTheme}>
            <HotYoterEligiblesPopupContent {...props} />
        </ThemeProvider>
    </CacheProvider>
);

export default HotYoterEligiblesPopup;
